import { Channel } from '@src/api/shared/community/channelApiClient';
import { LinkPreviewResponse, PinnedChat, ThreadChat } from '@src/api/shared/community/chatApiClient';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import ChatMessage, {
  ChatCallbacks,
  ChatContextData,
  ChatMenuPermission,
  ChatUIState,
  ChatUserState,
} from '@src/features/shared/community/components/chat/ChatMessage';
import useIsMobile from '@src/hooks/useIsMobile';
import React from 'react';

import { ChatItemWrapper } from '../chat/ChatItemWrapper';
import ChatUnreadLine from '../chat/ChatUnreadLine';

interface ThreadReplyItemProps {
  reply: ThreadChat;
  channel: Channel | null;
  channelVisibility?: number;
  pinnedChat?: PinnedChat;
  linkPreviewCache: Record<string, LinkPreviewResponse | null>;
  isLoggedIn: boolean;
  currentUserId?: number;
  currentUserType?: ChannelMemberUserTypeEnum;
  editingChatId: number | null;
  isActionMenuSpOpen: boolean;
  currentChatIdSp: number | undefined;
  onChatEditClick: (chat: ThreadChat) => void;
  onChatDeleteClick: (chat: ThreadChat) => void;
  onChatReportClick: (chat: ThreadChat) => void;
  onSetCurrentChatIdSp: (chatId: number) => void;
  onPinnedChatMenuClose: () => void;
  onPinnedChatLongPress: () => void;
  onToggleLinkPreview: (chatId: number, hidePreview: boolean) => void;
  onPinClick?: (chat_id: number | undefined) => Promise<void>;
  getChatMenuPermission: (chat: ThreadChat) => ChatMenuPermission;
  shouldShowUnreadLine: (chatId: number) => boolean;
  chatRef: (el: HTMLDivElement | null) => void;
  className?: string;
}

const ThreadReplyItem: React.FC<ThreadReplyItemProps> = ({
  reply,
  pinnedChat,
  linkPreviewCache,
  isLoggedIn,
  currentUserId,
  currentUserType,
  editingChatId,
  isActionMenuSpOpen,
  currentChatIdSp,
  onChatEditClick,
  onChatDeleteClick,
  onChatReportClick,
  onSetCurrentChatIdSp,
  onPinnedChatMenuClose,
  onPinnedChatLongPress,
  onToggleLinkPreview,
  onPinClick,
  getChatMenuPermission,
  shouldShowUnreadLine,
  chatRef,
}) => {
  const isMobile = useIsMobile();
  const contextData: ChatContextData = {
    chat: reply,
    linkPreviewCache,
    pinnedChat,
  };

  const userState: ChatUserState = {
    isLoggedIn,
    currentUserId,
    currentUserType,
  };

  const uiState: ChatUIState = {
    editingChatId,
    isActionMenuSpOpen,
    currentChatIdSp,
  };

  const callbacks: ChatCallbacks = {
    onPinClick: onPinClick || (async () => {}),
    onChatEditClick,
    onChatDeleteClick,
    onChatReportClick,
    onPinnedChatMenuClose,
    onSetCurrentChatIdSp,
    onPinnedChatLongPress,
    onToggleLinkPreview,
  };

  return (
    <div>
      <ChatUnreadLine
        chat={reply}
        shouldShowUnreadLine={shouldShowUnreadLine}
      />
      <ChatItemWrapper
        isPinned={pinnedChat?.id === reply.id}
        isMobile={isMobile}
        isEditing={editingChatId === reply.id}
      >
        <ChatMessage
          contextData={contextData}
          userState={userState}
          uiState={uiState}
          callbacks={callbacks}
          getChatMenuPermission={getChatMenuPermission}
          chatRef={chatRef}
        />
      </ChatItemWrapper>
    </div>
  );
};

export default ThreadReplyItem;
